import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import Button from "@/components/ui/button";
import { Close } from "@/components/icons";
import { virtualClassroomsApi } from "@/features/education-catalog/services/virtual-classrooms.api";
import type { VirtualClassroomFileResDto } from "@/features/education-catalog/types/virtual-classroom.types";
import { getFileDownloadUrl } from "@/lib/files.api";
import styles from "./VirtualClassroomFilesPanel.module.scss";

export interface VirtualClassroomFilesPanelProps {
  classroomId: string;
  /** Üst component file değişikliği bildirimini almak isterse (badge update vb.) */
  onChanged?: (files: VirtualClassroomFileResDto[]) => void;
}

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let n = bytes;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(n >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

const VirtualClassroomFilesPanel: React.FC<VirtualClassroomFilesPanelProps> = ({
  classroomId,
  onChanged,
}) => {
  const { t } = useTranslation();
  const [files, setFiles] = useState<VirtualClassroomFileResDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const fetchFiles = useCallback(async () => {
    if (!classroomId) return;
    setIsLoading(true);
    try {
      const items = await virtualClassroomsApi.listFiles(classroomId);
      setFiles(items);
      onChanged?.(items);
    } catch {
      // apiClient toast
    } finally {
      setIsLoading(false);
    }
  }, [classroomId, onChanged]);

  useEffect(() => {
    void fetchFiles();
  }, [fetchFiles]);

  const handleFileSelect = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    const list = e.target.files;
    if (!list || list.length === 0) return;
    const picked = Array.from(list);
    // Input'u temizle ki aynı dosya tekrar seçilebilsin.
    if (fileInputRef.current) fileInputRef.current.value = "";

    setIsUploading(true);
    try {
      await virtualClassroomsApi.uploadAndBindFiles(classroomId, picked);
      toast.success(t("virtualClassroomFiles.uploadSuccess"));
      await fetchFiles();
    } catch {
      // apiClient toast
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownload = async (
    fileId: string,
    originalName: string,
  ): Promise<void> => {
    try {
      const url = await getFileDownloadUrl(fileId);
      // Yeni sekmede indir / aç.
      const a = document.createElement("a");
      a.href = url;
      a.download = originalName;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch {
      // apiClient toast
    }
  };

  const handleDelete = async (fileId: string): Promise<void> => {
    setDeletingId(fileId);
    try {
      await virtualClassroomsApi.unbindFile(classroomId, fileId);
      toast.success(t("virtualClassroomFiles.deleteSuccess"));
      await fetchFiles();
    } catch {
      // apiClient toast
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className={styles.section}>
      <div className={styles.header}>
        <h4>{t("virtualClassroomFiles.title")}</h4>
        <div className={styles.uploadWrap}>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className={styles.hiddenInput}
            onChange={(e) => void handleFileSelect(e)}
            disabled={isUploading}
          />
          <Button
            variant="outlined"
            size="small"
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            loading={isUploading}
          >
            {t("virtualClassroomFiles.uploadButton")}
          </Button>
        </div>
      </div>

      {isLoading ? (
        <p className={styles.empty}>{t("common.loading")}</p>
      ) : files.length === 0 ? (
        <p className={styles.empty}>{t("virtualClassroomFiles.empty")}</p>
      ) : (
        <ul className={styles.list}>
          {files.map((f) => (
            <li key={f.id} className={styles.row}>
              <button
                type="button"
                className={styles.nameButton}
                onClick={() => void handleDownload(f.fileID, f.originalName)}
                title={t("virtualClassroomFiles.downloadHint")}
              >
                <span className={styles.name}>
                  {f.displayName || f.originalName}
                </span>
                <span className={styles.meta}>
                  {f.mimeType} · {formatBytes(f.size)}
                </span>
              </button>
              <button
                type="button"
                className={styles.removeButton}
                onClick={() => void handleDelete(f.fileID)}
                disabled={deletingId === f.fileID}
                aria-label={t("common.remove")}
                title={t("virtualClassroomFiles.deleteHint")}
              >
                <Close width={16} height={16} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default VirtualClassroomFilesPanel;
